import { useCallback, useEffect, useState } from "react";

import { WalletService } from "@/modules/dashboard/cards/services/walletsService";
import type { WalletTransactionEntity } from "@/modules/dashboard/cards/types/wallets-api.types";
import { HttpApiService } from "@/shared/services/httpApi.service";

import type { CardWalletMovementsFilters } from "../types/cardWallets.types";
import { DEFAULT_MOVEMENTS_FILTERS, toMovementsQuery } from "../utils/cardWalletMovements";

export interface UseCardWalletMovementsResult {
  movements: WalletTransactionEntity[];
  total: number;
  page: number;
  setPage: (page: number) => void;
  filters: CardWalletMovementsFilters;
  setFilters: (filters: CardWalletMovementsFilters) => void;
  clearFilters: () => void;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * The wallet's statement, paginated and filtered server-side.
 *
 * Every answer is matched against the request that asked for it, so a slow page cannot overwrite a newer one
 * — the filters change on every keystroke of the date picker and the selects fire immediately.
 */
export function useCardWalletMovements(walletId: string | null): UseCardWalletMovementsResult {
  const [movements, setMovements] = useState<WalletTransactionEntity[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(1);
  const [filters, setFiltersState] = useState<CardWalletMovementsFilters>(DEFAULT_MOVEMENTS_FILTERS);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [settledKey, setSettledKey] = useState<string | null>(null);

  const requestKey = walletId ? JSON.stringify({ walletId, page, filters, reloadKey }) : null;
  const isLoading = requestKey !== null && settledKey !== requestKey;

  useEffect(() => {
    if (!walletId || !requestKey) return;
    let cancelled = false;

    WalletService.getWalletTransactions(walletId, toMovementsQuery(filters, page))
      .then((res) => {
        if (cancelled) return;
        setMovements(res.data.data ?? []);
        setTotal(res.data.total ?? 0);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMovements([]);
        setTotal(0);
        setError(HttpApiService.getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setSettledKey(requestKey);
      });

    return () => {
      cancelled = true;
    };
  }, [walletId, page, filters, reloadKey, requestKey]);

  // A narrower filter can leave the current page past the end of the result set, so every filter change
  // returns to the first page — otherwise the list reads as empty when it is not.
  const setFilters = useCallback((next: CardWalletMovementsFilters) => {
    setFiltersState(next);
    setPageState(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_MOVEMENTS_FILTERS);
    setPageState(1);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return {
    movements,
    total,
    page,
    setPage: setPageState,
    filters,
    setFilters,
    clearFilters,
    isLoading,
    error,
    refresh,
  };
}
